import { useMemo } from 'react';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/system';
import { format } from 'date-fns';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TableContainer from '@mui/material/TableContainer';
import { Typography } from '@components/Typography';
import { Table } from '@components/Table';
import { useTranslation, useLocations, useTableSort } from '@hooks';
import { analyticsSurveyGroups } from '@helpers/surveyFilters';
import { analyticsService } from '@services';
import ExportCSV from '@features/Analytics/ExportCSV';
import { getTableBody } from './FeedbackTableBody';
import { getCollapsibleTableBody } from './CollapsibleRowBody';

const SORT_COLUMNS = {
    location: 'internalName',
    totalResponses: 'totalResponses',
    averageRating: 'averageRating',
    negativeResponses: 'negativeResponses',
    netPromoterScore: 'netPromoterScore',
};

const DEFAULT_SORT = { column: 'location', dir: 'asc' };

const GROUP_BY = { key: row => row.locationId, isParent: row => !!row.companyTotal };

export const PaperStyled = styled(Paper)({
    padding: '24px',
    width: '100%',
    borderRadius: '8px',
});

export const TableStyled = styled(Table)({
    '& tbody .MuiTableRow-root[data-row=true]': {
        backgroundColor: 'rgba(247, 251, 249)',
    },
});

export const TableCellText = styled(Typography)({
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
    alignItems: 'center',
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            width: '5%',
            component: <></>,
        },
        {
            key: 1,
            width: '23%',
            sortKey: 'location',
            align: 'left',
            component: (
                <Typography variant="subtitle2" align="left">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            width: '18%',
            sortKey: 'totalResponses',
            align: 'right',
            component: (
                <Typography variant="subtitle2" align="right">
                    {t('AnalyticsReviews.totalResponses')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: '18%',
            sortKey: 'averageRating',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.averageRating')}
                </Typography>
            ),
        },
        {
            key: 4,
            width: '18%',
            sortKey: 'negativeResponses',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.negativeResponses')}
                </Typography>
            ),
        },
        {
            key: 5,
            width: '18%',
            sortKey: 'netPromoterScore',
            align: 'right',
            component: (
                <Typography align="right" variant="subtitle2">
                    {t('AnalyticsReviews.nps')}
                </Typography>
            ),
        },
    ];
};

export const getTableArrow = field => {
    if (field > 0) {
        return <ArrowUpwardIcon sx={{ color: 'var(--success-main)', fontSize: '20px !important' }} />;
    } else if (field < 0) {
        return <ArrowDownwardIcon sx={{ color: 'var(--error-main)', fontSize: '20px !important' }} />;
    }
    return null;
};

const CollapsibleRow = ({ data, index }) => {
    return <Table rows={getCollapsibleTableBody(data, index)} />;
};

const ReviewsTable = ({ isLoading, data, filter, isCoverManagerConnected }) => {
    const { locationsMap } = useLocations();
    const { t } = useTranslation();

    const dataWithLocationNames = useMemo(
        () =>
            data.map(item => ({
                ...item,
                internalName: locationsMap[item.locationId]?.internalName || '',
            })),
        [data, locationsMap],
    );

    const { sort, onSortChange, sortedData } = useTableSort(dataWithLocationNames, {
        columns: SORT_COLUMNS,
        defaultSort: DEFAULT_SORT,
        groupBy: GROUP_BY,
    });

    const onExport = exportFormat =>
        analyticsService.exportAnalyticsCSV(
            {
                ...filter,
                ...analyticsSurveyGroups(filter.surveys, { isCoverManagerConnected }),
                locations: filter.locations.map(item => item.id),
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
            'feedbacks',
            null,
            exportFormat,
        );

    return (
        <PaperStyled elevation={1}>
            <div
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}
            >
                <Typography variant="title1" sx={{ alignSelf: 'center' }}>
                    {t(`Header.analyticsSurveys`)}
                </Typography>
                <ExportCSV type="feedbacks" apiCall={onExport} />
            </div>
            <TableContainer>
                <TableStyled
                    data={sortedData}
                    columnNames={getTableHeader(t)}
                    rows={getTableBody(sortedData)}
                    isDataLoading={isLoading}
                    isCollapsible={true}
                    CollapsibleRow={CollapsibleRow}
                    sort={sort}
                    onSortChange={onSortChange}
                    getRowKey={row => row.locationId}
                />
            </TableContainer>
        </PaperStyled>
    );
};

ReviewsTable.displayName = 'ReviewsTable';

export default ReviewsTable;
